import { getToken } from '../authentication/authProvider';

/** One box in a run, exactly as it stood when the run was built. */
export interface PickupParcel {
  funeralId: number;
  deceasedName: string;
  recipientName: string;
  city: string;
  province: string | null;
  trackingNumber: string | null;
  hasLabel: boolean;
}

/** A Rideau case ready to travel with no delivery day set. */
export interface DatelessCase {
  funeralId: number;
  deceasedName: string;
  cremationStatus: string;
  waitingDays: number;
}

export type RunOutcome = 'built' | 'sent' | 'send_skipped' | 'no_parcels' | 'failed';

/** A recorded send. Append-only: a resend adds a row, it never replaces one. */
export interface LabelRun {
  id: string;
  pickupDate: string;
  trigger: 'cron' | 'manual';
  triggeredBy: string | null;
  parcels: PickupParcel[];
  parcelCount: number;
  missingLabelCount: number;
  outcome: RunOutcome;
  error: string | null;
  /** Why the 4x6 PDF backup is missing from this run, when it is. The .zpl went without it. */
  pdfError: string | null;
  createdAt: string;
  hasZpl: boolean;
}

/**
 * Which side of today the panel is asking for. `upcoming` is pickups that have
 * not happened yet — today included — and it is what the panel shows by
 * default; `previous` is the same log looking back, behind a button.
 */
export type RunScope = 'upcoming' | 'previous';

/**
 * The panel's payload: the runs worth reading in the scope that was asked for,
 * and the proof the job behind them is alive. Days the cron found nothing to
 * collect are recorded but not listed — six in seven of them are empty and
 * listing them buried the real runs.
 */
export interface PickupRunHistory {
  runs: LabelRun[];
  /** When the build cron last wrote a row, whatever it found. Null before its first run. */
  lastRunAt: string | null;
}

/** What a run for a date would contain, without recording anything. */
export interface PickupRunPreview {
  pickupDate: string;
  parcels: PickupParcel[];
  skipped: { funeralId: number; reason: string }[];
  missingLabelCount: number;
  dateless: DatelessCase[];
}

const apiBase = () => {
  const apiUrl = process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001';
  return apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
};

const request = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
  const token = await getToken();
  const response = await fetch(`${apiBase()}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({} as { message?: string | string[] }));
    const message = Array.isArray(body.message) ? body.message.join('; ') : body.message;
    throw new Error(message || `Request failed (${response.status})`);
  }
  return response.json() as Promise<T>;
};

export const fetchPickupRuns = (scope: RunScope = 'upcoming', limit = 25) =>
  request<PickupRunHistory>(`/pickup-run/runs?scope=${scope}&limit=${limit}`);

export const previewPickupRun = (date: string) =>
  request<PickupRunPreview>(`/pickup-run/preview?date=${encodeURIComponent(date)}`);

/**
 * Rebuilds and re-sends the run for a date.
 *
 * Never buys a shipment: the bundle is rebuilt from labels already purchased. A
 * parcel with no label comes back reported as missing, not given one.
 */
export const resendPickupRun = (pickupDate: string) =>
  request<LabelRun>('/pickup-run/resend', {
    method: 'POST',
    body: JSON.stringify({ pickupDate }),
  });

/**
 * Downloads the run's 4x6 PDF — the stack as staff reprint it on an ordinary
 * printer.
 *
 * Re-rendered on request rather than stored: only the ZPL is kept, so this is
 * the same manifest and the same purchased labels drawn again, in the same
 * order. The runner's own copy still travels as the .zpl attached to his email.
 */
export const downloadRunPdf = async (run: LabelRun): Promise<void> => {
  const token = await getToken();
  const response = await fetch(`${apiBase()}/pickup-run/runs/${run.id}/pdf`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({} as { message?: string }));
    throw new Error(body.message || `Could not download the labels (${response.status})`);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `pickup-run-${run.pickupDate}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
